import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Grid,
  Input,
  List,
  Spin,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  AudioOutlined,
  CalendarOutlined,
  DownOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  StopOutlined,
  TeamOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PATHS } from "../../../app/router/paths";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import { PageHeader } from "../../../shared/components/PageHeader";
import { SellerSubmissionProductsEditor } from "../components/SellerSubmissionProductsEditor";
import {
  fetchPropose,
  fetchProposeCompanies,
  confirmPropose,
  rejectPropose,
  sendPropose,
  publishPropose,
  submitProposeSalesInput,
} from "../api/proposesApi";
import {
  getSellerProposeTemplateSubmission,
  saveSellerSubmissionRepeatableData,
  submitSellerProposeTemplateSubmission,
  type ProposeTemplateField,
  type ProposeTemplateRepeatableGroup,
  type ProposeTemplateTable,
  type SellerProposeTemplateSubmissionDetail,
} from "../api/proposeTemplatesApi";
import type { Propose } from "../model/types";
import { formatRepresentativeId } from "../model/proposeEditor";
import { statusColor, statusLabel } from "../model/proposeStatus";
import "../styles/Proposes.css";
import { useAuth } from "../../auth/model/AuthContext";
import VoiceInput from "../../../shared/components/VoiceInput";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";

const date = (value?: string | null) => value?.replace(/-/g, "/") || "—";
const number = (value?: number | string | null) =>
  value == null
    ? "-"
    : Number(value).toLocaleString("ja-JP", { maximumFractionDigits: 2 });
const hasText = (value?: string | null) => Boolean(value?.trim());
const text = (value?: string | null) => value?.trim() || "-";
const displaySubmissionValue = (value: unknown): string => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.map(displaySubmissionValue).join("、");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
const ellipsisText = (value?: string | number | null) => {
  const content = value == null || value === "" ? "-" : String(value);
  return (
    <Tooltip title={content}>
      <span className="propose-cell-ellipsis">{content}</span>
    </Tooltip>
  );
};
const japaneseWeekdays = ["日", "月", "火", "水", "木", "金", "土"];
const scheduleDateTitle = (deliveryDate: string) => (
  <div className="propose-schedule-date-heading">
    <span>{dayjs(deliveryDate).format("MM/DD")}</span>
    <small>（{japaneseWeekdays[dayjs(deliveryDate).day()]}）</small>
  </div>
);
const normalizePropose = (propose: Propose): Propose => ({
  ...propose,
  items: Array.isArray(propose.items)
    ? propose.items.map((item) => ({
        ...item,
        schedules: Array.isArray(item.schedules) ? item.schedules : [],
      }))
    : [],
});
const productInputFields = [
  "origin",
  "shipper",
  "scale",
  "grade",
  "size",
  "package",
  "quantity_per_package",
] as const;
const scheduleInputFields = ["quantity", "wholesale_price", "price"] as const;
const { useBreakpoint } = Grid;

export const ProposeViewPage = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const id = Number(params.get("id"));
  const isRecommendSubmission =
    (location.state as { recommendSubmission?: boolean } | null)?.recommendSubmission === true;
  const isFinishedSubmissionRoute =
    (location.state as { finishedSubmission?: boolean } | null)?.finishedSubmission === true;
  const [propose, setPropose] = useState<Propose>();
  const [sellerSubmission, setSellerSubmission] =
    useState<SellerProposeTemplateSubmissionDetail>();
  const [companyName, setCompanyName] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [part2Collapsed, setPart2Collapsed] = useState(true);
  const [part3Collapsed, setPart3Collapsed] = useState(true);
  const [nextTemporaryItemId, setNextTemporaryItemId] = useState(-1);
  const [pendingProductFocusItemId, setPendingProductFocusItemId] = useState<number>();
  const productInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const [activeVoiceItemIndex, setActiveVoiceItemIndex] = useState<number>();
  const [openedNoteRows, setOpenedNoteRows] = useState<number[]>([]);
  const [mobileEditingItemId, setMobileEditingItemId] = useState<number>();
  const [mobileItemSnapshot, setMobileItemSnapshot] =
    useState<Propose["items"][number]>();
  const [submissionHistoryItem, setSubmissionHistoryItem] = useState<Propose["items"][number]>();
  const isCoordinator = Boolean(
    user?.permissions.includes("propose.coordinator.manage"),
  );
  const isSeller = Boolean(
    user?.permissions.includes("propose.seller.contribute"),
  );
  const isDeadlineExpired = Boolean(
    propose?.expires_at && dayjs(propose.expires_at).valueOf() <= now,
  );
  const isSellerItemLeader = (item: Propose["items"][number]) =>
    Boolean(
      item.sellers?.some(
        (seller) => seller.seller_id === user?.user_id && seller.is_leader,
      ),
    );
  const hasLeaderItem = Boolean(propose?.items.some(isSellerItemLeader));
  const salesEditing = Boolean(
    isSeller &&
    params.get("mode") === "edit" &&
    propose?.status === "INPUTTING" &&
    hasLeaderItem &&
    !isDeadlineExpired,
  );
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!propose?.expires_at) return;
    const delay = Math.max(0, dayjs(propose.expires_at).diff(Date.now()));
    const timer = window.setTimeout(() => setNow(Date.now()), delay + 1);
    return () => window.clearTimeout(timer);
  }, [propose?.expires_at]);
  const loadPropose = useCallback(async () => {
    setCompanyName(undefined);
    if (!id) {
      setLoading(false);
      return;
    }
    if (isRecommendSubmission && isSeller && user?.user_id) {
      let active = true;
      setLoading(true);
      setPropose(undefined);
      setSellerSubmission(undefined);
      void getSellerProposeTemplateSubmission(String(user.user_id), id)
        .then((submission) => {
          if (active) setSellerSubmission(submission);
        })
        .catch((error) =>
          appNotification.error({ message: normalizeApiError(error).message }),
        )
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => {
        active = false;
      };
    }
    void fetchPropose(id)
      .then(async (proposal) => {
        setPropose(normalizePropose(proposal));
        if (!isCoordinator) return;
        const companies = await fetchProposeCompanies(
          1,
          10,
          proposal.representative_id,
        ).catch(() => undefined);
        const company = companies?.items.find(
          (item) => item.representative_id === proposal.representative_id,
        );
        setCompanyName(
          company?.company_out_name || company?.company_in_name || undefined,
        );
      })
      .catch((error) =>
        appNotification.error({ message: normalizeApiError(error).message }),
      )
      .finally(() => setLoading(false));
  }, [id, isCoordinator, isRecommendSubmission, isSeller, user?.user_id]);
  useEffect(() => {
    if (pendingProductFocusItemId == null) return;
    const timer = window.setTimeout(() => {
      productInputRefs.current[pendingProductFocusItemId]?.focus();
      setPendingProductFocusItemId(undefined);
    });
    return () => window.clearTimeout(timer);
  }, [pendingProductFocusItemId, propose?.items]);
  const dates = useMemo(() => {
    const scheduled = [
      ...new Set(
        (propose?.items ?? []).flatMap((item) =>
          item.schedules.map((schedule) => schedule.delivery_date),
        ) || [],
      ),
    ];
    if (!salesEditing) return scheduled.sort();
    if (!propose) return scheduled.sort();
    const start = dayjs(propose.effective_from);
    const end = dayjs(propose.effective_to);
    const days: string[] = [];
    for (let day = start; !day.isAfter(end, "day"); day = day.add(1, "day")) {
      days.push(day.format("YYYY-MM-DD"));
    }
    return days;
  }, [propose, salesEditing]);
  const itemNoteRows = useMemo(() => new Set(openedNoteRows), [openedNoteRows]);
  const parsedSubmissionPayload = useMemo(() => {
    const payload = sellerSubmission?.payload_json ?? sellerSubmission?.payload;
    if (typeof payload === "string") {
      try {
        const parsed: unknown = JSON.parse(payload);
        return parsed && typeof parsed === "object"
          ? (parsed as Record<string, unknown>)
          : {};
      } catch {
        return {};
      }
    }
    return payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : {};
  }, [sellerSubmission?.payload, sellerSubmission?.payload_json]);
  const payloadProductFields = Array.isArray(parsedSubmissionPayload.productFields)
    ? parsedSubmissionPayload.productFields as ProposeTemplateField[]
    : [];
  const payloadRepeatableGroups = Array.isArray(parsedSubmissionPayload.repeatableGroups)
    ? parsedSubmissionPayload.repeatableGroups as ProposeTemplateRepeatableGroup[]
    : [];
  const payloadTables = Array.isArray(parsedSubmissionPayload.tables)
    ? parsedSubmissionPayload.tables as ProposeTemplateTable[]
    : [];
  const payloadProducts = Array.isArray(parsedSubmissionPayload.products)
    ? parsedSubmissionPayload.products
    : [];

  if (isRecommendSubmission) {
    if (loading) {
      return <main className="propose-recommend-submission-view"><p>読み込み中…</p></main>;
    }
    if (!sellerSubmission) {
      return (
        <main className="propose-recommend-submission-view">
          <button type="button" className="propose-recommend-back" onClick={() => navigate(PATHS.PROPOSES.ROOT)}>
            一覧へ戻る
          </button>
          <p>提出内容を読み込めませんでした。</p>
        </main>
      );
    }

    const information = sellerSubmission.submission_information ?? {};
    const submissionFinished = isFinishedSubmissionRoute || sellerSubmission.status === "finished";
    const submissionStatus = submissionFinished
      ? "提出済み"
      : sellerSubmission.status === "edited"
        ? "編集中"
        : sellerSubmission.status === "inited"
          ? "入力待ち"
          : sellerSubmission.status === 1
            ? "公開中"
            : sellerSubmission.status === 0
              ? "下書き"
              : "—";
    return (
      <main className="propose-recommend-submission-view">
        <button type="button" className="propose-recommend-back" onClick={() => navigate(PATHS.PROPOSES.ROOT)}>
          一覧へ戻る
        </button>
        <header className="propose-recommend-submission-heading">
          <h1>{sellerSubmission.order_name || "提案詳細"}</h1>
          <p>{sellerSubmission.template_name || ""}</p>
        </header>
        <section className="propose-recommend-submission-section">
          <h2>提案基本情報</h2>
          <dl>
            <div><dt>Order Name</dt><dd>{displaySubmissionValue(sellerSubmission.order_name)}</dd></div>
            <div><dt>テンプレート名</dt><dd>{displaySubmissionValue(sellerSubmission.template_name)}</dd></div>
            <div><dt>買受人コード</dt><dd>{displaySubmissionValue(information.representative_id)}</dd></div>
            <div><dt>買受人担当者名</dt><dd>{displaySubmissionValue(information.representative_name)}</dd></div>
            <div><dt>営業入力期限</dt><dd>{information.deadline ? dayjs(String(information.deadline)).format("YYYY/MM/DD HH:mm") : "—"}</dd></div>
            <div><dt>状態</dt><dd>{submissionStatus}</dd></div>
            <div><dt>更新日時</dt><dd>{sellerSubmission.updated_at ? dayjs(sellerSubmission.updated_at).format("YYYY/MM/DD HH:mm") : "—"}</dd></div>
          </dl>
        </section>
        <SellerSubmissionProductsEditor
          key={sellerSubmission.id}
          submissionId={sellerSubmission.id}
          productFields={payloadProductFields}
          repeatableGroups={payloadRepeatableGroups}
          tables={payloadTables}
          products={payloadProducts}
          readOnly={submissionFinished || sellerSubmission.is_leader !== true}
          onSave={async (submissionId, products) => {
            try {
              if (!user?.user_id) throw new Error("ログイン中の営業ユーザーを確認できません。");
              await saveSellerSubmissionRepeatableData(
                String(user.user_id),
                submissionId,
                products,
              );
              setSellerSubmission((current) => current ? { ...current, status: "edited" } : current);
              appNotification.success({ message: "商品入力を保存しました" });
            } catch (error) {
              appNotification.error({ message: normalizeApiError(error).message });
            }
          }}
          onSubmit={async (submissionId, products) => {
            try {
              if (!user?.user_id) throw new Error("ログイン中の営業ユーザーを確認できません。");
              await submitSellerProposeTemplateSubmission(
                String(user.user_id),
                submissionId,
                { products },
              );
              setSellerSubmission((current) => current ? { ...current, status: "finished" } : current);
              appNotification.success({ message: "提出しました" });
            } catch (error) {
              appNotification.error({ message: normalizeApiError(error).message });
            }
          }}
        />
      </main>
    );
  }
  if (loading)
    return (
      <div className="propose-loading">
        <Spin />
      </div>
    );
  if (!propose) return <Empty description="提案書が見つかりません" />;
  const sendProposal = async () => {
    setSending(true);
    try {
      const updated = await publishPropose(propose.propose_id);
      setPropose(normalizePropose(updated));
      appNotification.success({ message: "提案を送信しました" });
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally {
      setSending(false);
    }
  };
  const confirmProposal = async () => {
    setSending(true);
    try {
      setPropose(normalizePropose(await confirmPropose(propose.propose_id)));
      appNotification.success({ message: "提案を確定しました" });
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally {
      setSending(false);
    }
  };
  const rejectProposal = async () => {
    setSending(true);
    try {
      setPropose(normalizePropose(await rejectPropose(propose.propose_id)));
      appNotification.success({ message: "提案を却下しました" });
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally { setSending(false); }
  };
  const sendToCustomer = async () => {
    setSending(true);
    try {
      setPropose(normalizePropose(await sendPropose(propose.propose_id)));
      appNotification.success({ message: "顧客へ送信しました" });
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally { setSending(false); }
  };
  const openSubmissionHistory = (item: Propose["items"][number]) =>
    setSubmissionHistoryItem(item);
  const submitSalesInput = async () => {
    if (invalidNumericInput) {
      appNotification.error({ message: "入力内容を確認してください" });
      return;
    }
    setSending(true);
    try {
      setPropose(
        normalizePropose(
          await submitProposeSalesInput(
            propose.propose_id,
            propose.items.filter(isSellerItemLeader),
          ),
        ),
      );
      appNotification.success({
        message: "営業入力を送信しました",
      });
    } catch (error) {
      const apiError = normalizeApiError(error);
      if (apiError.code === "PROPOSE_DEADLINE_EXPIRED") {
        void fetchPropose(propose.propose_id)
          .then(normalizePropose)
          .then(setPropose)
          .finally(() =>
            navigate(`${PATHS.PROPOSES.VIEW}?id=${propose.propose_id}`),
          );
      }
      appNotification.error({ message: apiError.message });
    } finally {
      setSending(false);
    }
  };
  const updateSellerItem = (
    itemId: number,
    changes: Partial<Propose["items"][number]>,
  ) => {
    setPropose(
      (current) =>
        current && {
          ...current,
          items: current.items.map((item) =>
            item.propose_item_id === itemId ? { ...item, ...changes } : item,
          ),
        },
    );
  };
  const addSellerItemCopy = (source: Propose["items"][number], openMobile = false) => {
    const temporaryId = nextTemporaryItemId;
    setNextTemporaryItemId((current) => current - 1);
    setPropose(
      (current) =>
        current && {
          ...current,
          items: (() => {
            const inserted = {
              propose_item_id: temporaryId,
              product_code: source.product_code,
              product_name: source.product_name,
              sellers: source.sellers,
              origin: null,
              shipper: null,
              grade: null,
              size: null,
              scale: null,
              package: null,
              quantity_per_package: null,
              note: null,
              schedules: [],
            };
            const index = current.items.findIndex(
              (item) => item.propose_item_id === source.propose_item_id,
            );
            return [
              ...current.items.slice(0, index + 1),
              inserted,
              ...current.items.slice(index + 1),
            ];
          })(),
        },
    );
    setPendingProductFocusItemId(temporaryId);
    if (openMobile) {
      setMobileItemSnapshot(undefined);
      setMobileEditingItemId(temporaryId);
    }
    appNotification.success({ message: "同じ商品の入力行を追加しました" });
  };
  const removeSellerItem = (item: Propose["items"][number]) => {
    const index = propose.items.findIndex(
      (candidate) => candidate.propose_item_id === item.propose_item_id,
    );
    const nextItem = propose.items[index + 1] ?? propose.items[index - 1];
    setPropose((current) =>
      current && {
        ...current,
        items: current.items.filter(
          (candidate) => candidate.propose_item_id !== item.propose_item_id,
        ),
      },
    );
    if (nextItem) setPendingProductFocusItemId(nextItem.propose_item_id!);
    setMobileEditingItemId(undefined);
    setMobileItemSnapshot(undefined);
  };
  const canDeleteSellerItem = (item: Propose["items"][number]) =>
    propose.items.filter((candidate) => candidate.product_code === item.product_code)
      .length >= 2;
  const requestRemoveSellerItem = (item: Propose["items"][number]) =>
    Modal.confirm({
      title: "この商品行を削除しますか？",
      content: "保存時に削除内容が反映されます。",
      onOk: () => removeSellerItem(item),
    });
  const updateSellerSchedule = (
    item: Propose["items"][number],
    deliveryDate: string,
    changes: Partial<Propose["items"][number]["schedules"][number]>,
  ) => {
    const existing = item.schedules.find(
      (schedule) => schedule.delivery_date === deliveryDate,
    );
    const next = {
      delivery_date: deliveryDate,
      quantity: existing?.quantity ?? 0,
      wholesale_price: existing?.wholesale_price ?? null,
      price: existing?.price ?? null,
      ...changes,
    };
    const schedules = item.schedules.filter(
      (schedule) => schedule.delivery_date !== deliveryDate,
    );
    if (Number(next.quantity) > 0) schedules.push(next);
    updateSellerItem(item.propose_item_id!, { schedules });
  };
  const moveProductInputFocus = (
    event: KeyboardEvent<HTMLInputElement>,
    row: number,
    column: number,
  ) => {
    const next = {
      ArrowLeft: [row, column - 1],
      ArrowRight: [row, column + 1],
      ArrowUp: [row - 1, column],
      ArrowDown: [row + 1, column],
    }[event.key];
    if (
      !next ||
      next[0] < 0 ||
      next[0] >= propose.items.length ||
      next[1] < 0 ||
      next[1] >= productInputFields.length
    )
      return;
    event.preventDefault();
    document
      .getElementById(`propose-item-input-${next[0]}-${next[1]}`)
      ?.focus();
  };
  const moveScheduleInputFocus = (
    event: KeyboardEvent<HTMLInputElement>,
    row: number,
    dateIndex: number,
    field: (typeof scheduleInputFields)[number],
  ) => {
    const isHorizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
    const isVertical = event.key === "ArrowUp" || event.key === "ArrowDown";
    if (!isHorizontal && !isVertical) return;

    let targetRow = row;
    let targetDateIndex = dateIndex;
    let targetFieldIndex = scheduleInputFields.indexOf(field);
    const direction = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    const limit = isHorizontal ? dates.length : propose.items.length * scheduleInputFields.length;

    for (let step = 0; step < limit; step += 1) {
      if (isHorizontal) {
        targetDateIndex += direction;
      } else if (event.key === "ArrowDown") {
        targetFieldIndex += 1;
        if (targetFieldIndex === scheduleInputFields.length) {
          targetFieldIndex = 0;
          targetRow += 1;
        }
      } else {
        targetFieldIndex -= 1;
        if (targetFieldIndex < 0) {
          targetFieldIndex = scheduleInputFields.length - 1;
          targetRow -= 1;
        }
      }

      if (
        targetRow < 0 ||
        targetRow >= propose.items.length ||
        targetDateIndex < 0 ||
        targetDateIndex >= dates.length
      )
        return;

      const targetField = scheduleInputFields[targetFieldIndex];
      const target = document.getElementById(
        `propose-schedule-input-${targetRow}-${targetDateIndex}-${targetField}`,
      ) as HTMLInputElement | null;
      if (!target || target.disabled) continue;

      event.preventDefault();
      target.focus();
      return;
    }
  };
  const updateScheduleField = (
    item: Propose["items"][number],
    deliveryDate: string,
    field: "quantity" | "wholesale_price" | "price",
    value: string,
  ) => {
    if (!/^\d*$/.test(value)) return;
    updateSellerSchedule(
      item,
      deliveryDate,
      { [field]: value === "" ? (field === "quantity" ? 0 : null) : Number(value) },
    );
  };
  const discardMobileItemChanges = () => {
    if ((mobileEditingItemId ?? 0) < 0) {
      const item = propose.items.find(
        (candidate) => candidate.propose_item_id === mobileEditingItemId,
      );
      if (item) removeSellerItem(item);
      return;
    }
    if (mobileItemSnapshot) {
      updateSellerItem(mobileItemSnapshot.propose_item_id!, mobileItemSnapshot);
    }
    setMobileItemSnapshot(undefined);
    setMobileEditingItemId(undefined);
  };
  const salesEditable = salesEditing;
  const openSalesEditor = () =>
    navigate(`${PATHS.PROPOSES.VIEW}?id=${propose.propose_id}&mode=edit`);
  const cancelSalesEditor = () => navigate(PATHS.PROPOSES.ROOT);
  const backToList = () =>
    navigate(
      isCoordinator
        ? `${PATHS.PROPOSES.ROOT}?representative_id=${encodeURIComponent(propose.representative_id)}`
        : PATHS.PROPOSES.ROOT,
    );
  const numericError = (
    value: number | string | null | undefined,
    _integer: boolean,
  ) => {
    const textValue = String(value ?? "").trim();
    if (!textValue) return undefined;
    return /^\d+$/.test(textValue)
      ? undefined
      : "0以上の整数を入力してください";
  };
  const sellerNumericInput = (
    item: Propose["items"][number],
    field: "quantity_per_package",
    id?: string,
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void,
  ) => {
    const error = numericError(
      item[field],
      true,
    );
    return (
      <div className="propose-numeric-field">
        <Input
          id={id}
          inputMode="numeric"
          status={error ? "error" : undefined}
          value={item[field] ?? ""}
          aria-invalid={Boolean(error)}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            if (!/^\d*$/.test(event.target.value)) return;
            updateSellerItem(item.propose_item_id!, {
              [field]: event.target.value || null,
            });
          }}
        />
        {error && <span className="propose-numeric-error" role="alert">{error}</span>}
      </div>
    );
  };
  const invalidNumericInput = propose.items
    .filter(isSellerItemLeader)
    .some((item) =>
    [
      numericError(item.quantity_per_package, true),
      ...item.schedules.flatMap((schedule) => [
        numericError(schedule.wholesale_price, true),
        numericError(schedule.price, true),
      ]),
    ].some(Boolean),
  );
  const toggleNote = (itemId: number) =>
    setOpenedNoteRows((current) =>
      current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId],
    );
  const requestPublish = () =>
    Modal.confirm({
      title: "営業へ公開しますか？",
      content: "公開後は提案内容を編集できません。",
      onOk: sendProposal,
    });
  const requestSubmit = () =>
    Modal.confirm({
      title: "営業入力を送信しますか？",
      content: "送信後も他の営業担当者は入力を続けられます。",
      onOk: submitSalesInput,
    });
  const requestConfirm = () =>
    Modal.confirm({
      title: "提案を最終確定しますか？",
      content: "確定後は承認済みになります。",
      onOk: confirmProposal,
    });
  const mobileProductPanels = propose.items.map((item, index) => ({
    key: String(item.propose_item_id ?? index),
    label: (
      <div className="propose-mobile-collapse-title">
        <strong>
          {index + 1}. {text(item.product_name)}
        </strong>
      </div>
    ),
    children: (
      <>
        <div className="propose-mobile-detail-grid">
          <span>商品コード</span>
          <strong>{item.product_code}</strong>
          <span>産地</span>
          <strong>{text(item.origin)}</strong>
          <span>出荷者</span>
          <strong>{text(item.shipper)}</strong>
          <span>重量</span>
          <strong>{text(item.scale)}</strong>
          <span>等級 / 階級</span>
          <strong>{`${text(item.grade)} / ${text(item.size)}`}</strong>
          <span>荷袋</span>
          <strong>{text(item.package)}</strong>
          <span>入数</span>
          <strong>{number(item.quantity_per_package)}</strong>
          {hasText(item.note) && (
            <>
              <span>備考</span>
              <strong>{text(item.note)}</strong>
            </>
          )}
        </div>
        <div className="propose-mobile-submission-history">
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => openSubmissionHistory(item)}
          >
            担当営業・提出履歴
          </Button>
        </div>
        {salesEditable && isSellerItemLeader(item) && (
          <div className="propose-mobile-product-panel-actions">
            <Button icon={<PlusOutlined />} onClick={() => addSellerItemCopy(item, true)}>
              追加
            </Button>
            <Button icon={<EditOutlined />} onClick={() => {
              setMobileItemSnapshot({
                ...item,
                schedules: item.schedules.map((schedule) => ({ ...schedule })),
              });
              setMobileEditingItemId(item.propose_item_id);
            }}>
              編集
            </Button>
            {canDeleteSellerItem(item) && (
              <Button danger icon={<DeleteOutlined />} onClick={() => requestRemoveSellerItem(item)}>
                削除
              </Button>
            )}
          </div>
        )}
      </>
    ),
  }));
  const mobileSchedulePanels = propose.items.map((item, index) => {
    const total = item.schedules.reduce(
      (sum, schedule) => sum + Number(schedule.quantity || 0),
      0,
    );
    const scheduledDays = item.schedules
      .filter((schedule) => Number(schedule.quantity) > 0)
      .sort((left, right) =>
        left.delivery_date.localeCompare(right.delivery_date),
      );
    const displayedDays = salesEditable && isSellerItemLeader(item)
      ? dates.map((delivery_date) => ({
          delivery_date,
          quantity:
            item.schedules.find(
              (schedule) => schedule.delivery_date === delivery_date,
            )?.quantity ?? 0,
          wholesale_price:
            item.schedules.find(
              (schedule) => schedule.delivery_date === delivery_date,
            )?.wholesale_price ?? null,
          price:
            item.schedules.find(
              (schedule) => schedule.delivery_date === delivery_date,
            )?.price ?? null,
        }))
      : scheduledDays;
    return {
      key: String(item.propose_item_id ?? index),
      label: (
        <div className="propose-mobile-collapse-title">
          <strong>
            {index + 1}. {text(item.product_name)}
          </strong>
          <div className="propose-mobile-total">
            <span>合計数量</span>
            <strong>{number(total)}</strong>
          </div>
        </div>
      ),
      children: (
        <div className="propose-mobile-schedule-list">
          {displayedDays.length ? (
            displayedDays.map((schedule) => (
              <div
                key={schedule.delivery_date}
                className="propose-mobile-schedule-row"
              >
                <div className="propose-mobile-schedule-date">
                  <span>{dayjs(schedule.delivery_date).format("MM/DD")}</span>
                  <small>
                    （{japaneseWeekdays[dayjs(schedule.delivery_date).day()]}）
                  </small>
                </div>
                {salesEditable && isSellerItemLeader(item) ? (
                  <div className="propose-mobile-schedule-inputs">
                    <div className="propose-mobile-schedule-input-row">
                      <span>数量</span>
                      <Input
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={schedule.quantity ?? ""}
                        onChange={(event) =>
                          updateScheduleField(
                            item,
                            schedule.delivery_date,
                            "quantity",
                            event.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="propose-mobile-schedule-input-row">
                      <span>仕切価格</span>
                      <Input
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={!Number(schedule.quantity)}
                        value={schedule.wholesale_price ?? ""}
                        onChange={(event) =>
                          updateScheduleField(
                            item,
                            schedule.delivery_date,
                            "wholesale_price",
                            event.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="propose-mobile-schedule-input-row">
                      <span>提案価格</span>
                      <Input
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={!Number(schedule.quantity)}
                        value={schedule.price ?? ""}
                        onChange={(event) =>
                          updateScheduleField(
                            item,
                            schedule.delivery_date,
                            "price",
                            event.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="propose-mobile-schedule-values">
                    <strong>数量: {number(schedule.quantity)}</strong>
                    <strong>仕切: {number(schedule.wholesale_price)}</strong>
                    <strong>提案: {number(schedule.price)}</strong>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="propose-mobile-schedule-empty">
              <CalendarOutlined />
              <span>供給予定はありません</span>
            </div>
          )}
        </div>
      ),
    };
  });
  if (isMobile && salesEditing) {
    const editingItem = propose.items.find(
      (item) => item.propose_item_id === mobileEditingItemId,
    );
    if (editingItem) {
      return (
        <main className="propose-mobile-editor-page propose-mobile-product-form">
          <VoiceInput
            onCompleted={({ transcript, language }) => {
              console.log("Voice input completed", {
                itemId: editingItem.propose_item_id,
                transcript,
                language,
              });
            }}
          >
            {(voiceControls) => {
              const isActive =
                activeVoiceItemIndex === editingItem.propose_item_id;
              return (
                <div className="propose-mobile-product-form-heading">
                  <h1>商品を編集</h1>
                  <Button
                    type="text"
                    icon={
                      isActive && voiceControls.isRecording ? (
                        <StopOutlined />
                      ) : (
                        <AudioOutlined />
                      )
                    }
                    aria-label={
                      isActive && voiceControls.isRecording
                        ? "音声入力を停止"
                        : "音声入力を開始"
                    }
                    disabled={
                      voiceControls.isPanelOpen &&
                      (!isActive || !voiceControls.isRecording)
                    }
                    onClick={() => {
                      if (isActive && voiceControls.isRecording)
                        voiceControls.stopRecording();
                      else if (!voiceControls.isPanelOpen) {
                        setActiveVoiceItemIndex(editingItem.propose_item_id);
                        void voiceControls.startRecording();
                      }
                    }}
                  />
                </div>
              );
            }}
          </VoiceInput>
          <Form layout="vertical" className="propose-mobile-form-content">
            <Form.Item label="商品名">
              <Input
                value={`${editingItem.product_code} - ${text(editingItem.product_name)}`}
                disabled
              />
            </Form.Item>
            <Form.Item label="産地">
              <Input
                value={editingItem.origin ?? ""}
                onChange={(event) =>
                  updateSellerItem(editingItem.propose_item_id!, {
                    origin: event.target.value || null,
                  })
                }
              />
            </Form.Item>
            <Form.Item label="出荷者">
              <Input
                value={editingItem.shipper ?? ""}
                onChange={(event) =>
                  updateSellerItem(editingItem.propose_item_id!, {
                    shipper: event.target.value || null,
                  })
                }
              />
            </Form.Item>
            <div className="propose-mobile-form-grid">
              <Form.Item label="重量">
                <Input
                  value={editingItem.scale ?? ""}
                  onChange={(event) =>
                    updateSellerItem(editingItem.propose_item_id!, {
                      scale: event.target.value || null,
                    })
                  }
                />
              </Form.Item>
              <Form.Item label="等級">
                <Input
                  value={editingItem.grade ?? ""}
                  onChange={(event) =>
                    updateSellerItem(editingItem.propose_item_id!, {
                      grade: event.target.value || null,
                    })
                  }
                />
              </Form.Item>
              <Form.Item label="階級">
                <Input
                  value={editingItem.size ?? ""}
                  onChange={(event) =>
                    updateSellerItem(editingItem.propose_item_id!, {
                      size: event.target.value || null,
                    })
                  }
                />
              </Form.Item>
              <Form.Item
                label="入数"
                validateStatus={numericError(editingItem.quantity_per_package, true) ? "error" : undefined}
                help={numericError(editingItem.quantity_per_package, true)}
              >
                {sellerNumericInput(editingItem, "quantity_per_package")}
              </Form.Item>
            </div>
            <Form.Item label="荷袋">
              <Input
                value={editingItem.package ?? ""}
                onChange={(event) =>
                  updateSellerItem(editingItem.propose_item_id!, {
                    package: event.target.value || null,
                  })
                }
              />
            </Form.Item>
            <Form.Item label="商品備考">
              <Input
                value={editingItem.note ?? ""}
                placeholder="商品のメモを入力..."
                onChange={(event) =>
                  updateSellerItem(editingItem.propose_item_id!, {
                    note: event.target.value || null,
                  })
                }
              />
            </Form.Item>
          </Form>
          <div className="propose-mobile-form-actions">
            <Button onClick={discardMobileItemChanges}>キャンセル</Button>
            <Button
              type="primary"
              onClick={() => {
                setMobileItemSnapshot(undefined);
                setMobileEditingItemId(undefined);
              }}
            >
              商品を保存
            </Button>
          </div>
        </main>
      );
    }
    return (
      <main className="propose-mobile-editor-page">
        <h1>営業入力を編集</h1>
        <Tabs
          className="propose-mobile-tabs"
          centered
          items={[
            {
              key: "products",
              label: "商品",
              children: (
                <Collapse
                  className="propose-mobile-product-collapse"
                  accordion
                  defaultActiveKey={mobileProductPanels[0]?.key}
                  expandIconPosition="end"
                  items={mobileProductPanels}
                />
              ),
            },
            {
              key: "schedule",
              label: "供給スケジュール",
              children: (
                <div className="propose-mobile-schedule-tab propose-mobile-sales-schedule">
                  <Collapse
                    accordion
                    expandIconPosition="end"
                    items={mobileSchedulePanels}
                  />
                </div>
              ),
            },
          ]}
        />
        <div className="propose-mobile-editor-actions">
          <Button onClick={cancelSalesEditor}>キャンセル</Button>
          <Button type="primary" loading={sending} disabled={invalidNumericInput} onClick={requestSubmit}>
            入力を送信
          </Button>
        </div>
        <Modal
          open={Boolean(submissionHistoryItem)}
          title={`担当営業・提出履歴: ${submissionHistoryItem?.product_code ?? ""}`}
          footer={null}
          onCancel={() => {
            setSubmissionHistoryItem(undefined);
          }}
        >
          <List
            dataSource={submissionHistoryItem?.sellers ?? []}
            renderItem={(seller) => (
              <List.Item>
                <div>
                  <strong>{seller.display_name || `営業 ${seller.seller_id}`}</strong>
                  <div>
                    {seller.submitted_at
                      ? `提出済み: ${dayjs(seller.submitted_at).format("YYYY/MM/DD HH:mm")}`
                      : "未提出"}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Modal>
      </main>
    );
  }
  if (isMobile)
    return (
      <main className="propose-mobile-view-page">
        <header className="propose-mobile-view-header">
          <Button
            type="text"
            className="propose-mobile-view-back"
            icon={<ArrowLeftOutlined />}
            onClick={backToList}
          >
            一覧へ戻る
          </Button>
          <div className="propose-mobile-view-code">
            <h1>{propose.propose_code}</h1>
            <Tag color={statusColor(propose.status)}>
              {statusLabel(propose.status)}
            </Tag>
          </div>
          <strong className="propose-mobile-view-company">
            {companyName || formatRepresentativeId(propose.representative_id)}
          </strong>
          <div className="propose-mobile-view-recipient">
            <strong>{text(propose.recipient_name)}</strong>
            <Tag color="green">
              買受人コード {formatRepresentativeId(propose.representative_id)}
            </Tag>
          </div>
          <div className="propose-mobile-view-dates">
            <span>
              {date(propose.effective_from)} ～ {date(propose.effective_to)}
            </span>
            <span>出荷日: {date(propose.estimated_delivery_date)}</span>
            <span>店着予定日: {date(propose.estimated_receipt_date)}</span>
          </div>
        </header>
        <Tabs
          className="propose-mobile-tabs"
          centered
          items={[
            {
              key: "products",
              label: "商品",
              children: (
                <Collapse
                  className="propose-mobile-product-collapse"
                  accordion
                  defaultActiveKey={mobileProductPanels[0]?.key}
                  expandIconPosition="end"
                  items={mobileProductPanels}
                />
              ),
            },
            {
              key: "schedule",
              label: "供給スケジュール",
              children: (
                <div className="propose-mobile-schedule-tab">
                  <Collapse
                    accordion
                    defaultActiveKey={mobileSchedulePanels[0]?.key}
                    expandIconPosition="end"
                    items={mobileSchedulePanels}
                  />
                </div>
              ),
            },
          ]}
        />
        <div className="propose-mobile-actions">
          {isCoordinator && propose.status === "DRAFT" && (
            <Button
              onClick={() =>
                navigate(`${PATHS.PROPOSES.EDIT}?id=${propose.propose_id}`)
              }
            >
              編集
            </Button>
          )}
          {isCoordinator && propose.status === "DRAFT" && (
            <Button
              type="primary"
              style={{ color: "#fff" }}
              loading={sending}
              disabled={propose.status !== "DRAFT"}
              onClick={requestPublish}
            >
              提案送信
            </Button>
          )}
          {isSeller &&
            propose.status === "INPUTTING" &&
            hasLeaderItem &&
            !salesEditing && !isDeadlineExpired && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={openSalesEditor}
              >
                入力を編集
              </Button>
            )}
          {salesEditing && (
            <Button onClick={cancelSalesEditor}>キャンセル</Button>
          )}
          {salesEditing && (
            <Button type="primary" loading={sending} disabled={invalidNumericInput} onClick={requestSubmit}>
              入力を送信
            </Button>
          )}
          {isCoordinator && propose.status === "REVIEW" && (<><Button type="primary" loading={sending} onClick={requestConfirm}>承認</Button><Button danger loading={sending} onClick={() => Modal.confirm({ title: "提案を却下しますか？", onOk: rejectProposal })}>却下</Button></>)}
          {isCoordinator && propose.status === "APPROVED" && (<Button type="primary" loading={sending} onClick={() => Modal.confirm({ title: "顧客へ送信しますか？", onOk: sendToCustomer })}>顧客へ送信</Button>)}
        </div>
        <Modal open={Boolean(submissionHistoryItem)} title={`担当営業・提出履歴: ${submissionHistoryItem?.product_code ?? ""}`} footer={null} onCancel={() => setSubmissionHistoryItem(undefined)}>
          <List
            dataSource={submissionHistoryItem?.sellers ?? []}
            renderItem={(seller) => <List.Item><div><strong>{seller.display_name || `営業 ${seller.seller_id}`}</strong><div>{seller.submitted_at ? `提出済み: ${dayjs(seller.submitted_at).format("YYYY/MM/DD HH:mm")}` : "未提出"}</div></div></List.Item>}
          />
        </Modal>
      </main>
    );
  return (
    <main className="propose-editor propose-view-page">
      <PageHeader
        title={
          salesEditing
            ? `営業入力を編集: ${propose.propose_code}`
            : `提案書: ${propose.propose_code}`
        }
        description={
          salesEditing
            ? "担当商品の内容と供給予定を入力します"
            : "提案内容を確認します"
        }
        isbutton={false}
        actions={<div className="propose-view-header-actions">
          {!salesEditing && (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={backToList}
            >
              一覧へ戻る
            </Button>
          )}
          {isCoordinator && propose.status === "DRAFT" && (
            <Button
              style={{ color: "#fff", backgroundColor: "#1677ff" }}
              icon={<EditOutlined />}
              onClick={() =>
                navigate(`${PATHS.PROPOSES.EDIT}?id=${propose.propose_id}`)
              }
            >
              編集
            </Button>
          )}
          {isSeller &&
            propose.status === "INPUTTING" &&
            hasLeaderItem &&
            !salesEditing && !isDeadlineExpired && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={openSalesEditor}
              >
                入力を編集
              </Button>
            )}
          {salesEditing && (
            <Button onClick={cancelSalesEditor}>キャンセル</Button>
          )}
          {salesEditing && (
            <Button type="primary" loading={sending} disabled={invalidNumericInput} onClick={requestSubmit}>
              入力を送信
            </Button>
          )}
          {isCoordinator && propose.status === "REVIEW" && (<><Button type="primary" loading={sending} onClick={requestConfirm}>承認</Button><Button danger loading={sending} onClick={() => Modal.confirm({ title: "提案を却下しますか？", onOk: rejectProposal })}>却下</Button></>)}
          {isCoordinator && propose.status === "APPROVED" && (<Button type="primary" loading={sending} onClick={() => Modal.confirm({ title: "顧客へ送信しますか？", onOk: sendToCustomer })}>顧客へ送信</Button>)}
        </div>}
      />
      <section className="propose-section">
        <h3>パート1 — 提案基本情報</h3>
        <Descriptions className="propose-view-summary" column={3} size="small" layout="vertical">
          <Descriptions.Item label="買受人コード">
            {formatRepresentativeId(propose.representative_id)}
          </Descriptions.Item>
          <Descriptions.Item label="買受人担当者名">
            {text(propose.recipient_name)}
          </Descriptions.Item>
          <Descriptions.Item label="状態">
            <span className="propose-view-status">
              <Tag color={statusColor(propose.status)}>
                {statusLabel(propose.status)}
              </Tag>
            </span>
          </Descriptions.Item>
          {propose.expires_at && (
            <Descriptions.Item label="営業入力期限">
              <span className="propose-view-deadline">
                {dayjs(propose.expires_at).format("YYYY/MM/DD HH:mm")}
              </span>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="有効期間">
            {date(propose.effective_from)} ～ {date(propose.effective_to)}
          </Descriptions.Item>
          <Descriptions.Item label="納品予定日">
            {propose.estimated_delivery_date
              ? date(propose.estimated_delivery_date)
              : ""}
          </Descriptions.Item>
          <Descriptions.Item label="受取予定日">
            {propose.estimated_receipt_date
              ? date(propose.estimated_receipt_date)
              : ""}
          </Descriptions.Item>
          {hasText(propose.note) && (
            <Descriptions.Item label="備考" span={3}>
              {text(propose.note)}
            </Descriptions.Item>
          )}
        </Descriptions>
      </section>
      <section className="propose-section">
        <h3
          className="propose-collapsible-section-title"
          role="button"
          tabIndex={0}
          onClick={() => setPart2Collapsed((collapsed) => !collapsed)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setPart2Collapsed((collapsed) => !collapsed);
            }
          }}
        >
          パート2 — 提案商品一覧
          <Button
            type="text"
            size="small"
            icon={part2Collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              setPart2Collapsed((collapsed) => !collapsed);
            }}
          >
            {part2Collapsed ? "表示" : "非表示"}
          </Button>
        </h3>
        {!part2Collapsed && (
          <Table
            className="propose-edit-table propose-view-schedule-table"
            rowKey="propose_item_id"
            pagination={false}
            scroll={{ x: 1120 }}
            dataSource={propose.items}
            expandable={{
              showExpandColumn: false,
              expandedRowKeys: Array.from(itemNoteRows),
              expandedRowRender: (item) => (
                <div className="propose-view-note">
                  {salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      value={item.note ?? ""}
                      placeholder="備考を入力"
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          note: event.target.value || null,
                        })
                      }
                    />
                  ) : (
                    text(item.note)
                  )}
                </div>
              ),
            }}
            columns={[
              {
                title: "商品",
                fixed: "left",
                width: 185,
                render: (_, item) => (
                  <strong>{ellipsisText(`${item.product_code} - ${text(item.product_name)}`)}</strong>
                ),
              },
              {
                title: "産地",
                dataIndex: "origin",
                width: 100,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      id={`propose-item-input-${row}-0`}
                      ref={(node) => {
                        productInputRefs.current[item.propose_item_id!] = node?.input ?? null;
                      }}
                      value={value ?? ""}
                      onKeyDown={(event) =>
                        moveProductInputFocus(event, row, 0)
                      }
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          origin: event.target.value || null,
                        })
                      }
                    />
                  ) : ellipsisText(text(value)),
              },
              {
                title: "出荷者",
                dataIndex: "shipper",
                width: 110,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      id={`propose-item-input-${row}-1`}
                      value={value ?? ""}
                      onKeyDown={(event) =>
                        moveProductInputFocus(event, row, 1)
                      }
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          shipper: event.target.value || null,
                        })
                      }
                    />
                  ) : ellipsisText(text(value)),
              },
              {
                title: "重量",
                dataIndex: "scale",
                width: 72,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      id={`propose-item-input-${row}-2`}
                      value={value ?? ""}
                      onKeyDown={(event) =>
                        moveProductInputFocus(event, row, 2)
                      }
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          scale: event.target.value || null,
                        })
                      }
                    />
                  ) : ellipsisText(text(value)),
              },
              {
                title: "等級",
                dataIndex: "grade",
                width: 72,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      id={`propose-item-input-${row}-3`}
                      value={value ?? ""}
                      onKeyDown={(event) =>
                        moveProductInputFocus(event, row, 3)
                      }
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          grade: event.target.value || null,
                        })
                      }
                    />
                  ) : ellipsisText(text(value)),
              },
              {
                title: "階級",
                dataIndex: "size",
                width: 72,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      id={`propose-item-input-${row}-4`}
                      value={value ?? ""}
                      onKeyDown={(event) =>
                        moveProductInputFocus(event, row, 4)
                      }
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          size: event.target.value || null,
                        })
                      }
                    />
                  ) : ellipsisText(text(value)),
              },
              {
                title: "荷袋",
                dataIndex: "package",
                width: 72,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    <Input
                      id={`propose-item-input-${row}-5`}
                      value={value ?? ""}
                      onKeyDown={(event) =>
                        moveProductInputFocus(event, row, 5)
                      }
                      onChange={(event) =>
                        updateSellerItem(item.propose_item_id!, {
                          package: event.target.value || null,
                        })
                      }
                    />
                  ) : ellipsisText(text(value)),
              },
              {
                title: "入数",
                dataIndex: "quantity_per_package",
                width: 88,
                render: (value, item, row) =>
                  salesEditable && isSellerItemLeader(item) ? (
                    sellerNumericInput(
                      item,
                      "quantity_per_package",
                      `propose-item-input-${row}-6`,
                      (event) => moveProductInputFocus(event, row, 6),
                    )
                  ) : (
                    number(value)
                  ),
              },
              {
                title: "",
                width: salesEditable ? 136 : 64,
                fixed: "right",
                render: (_value: unknown, item: Propose["items"][number]) => (
                  <Space size={0}>
                    <Button
                      type="text"
                      icon={<TeamOutlined />}
                      aria-label="担当営業・提出履歴"
                      title="担当営業・提出履歴"
                      onClick={() => openSubmissionHistory(item)}
                    />
                    {salesEditable && isSellerItemLeader(item) && (
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        aria-label="同じ商品を追加"
                        title="同じ商品を追加"
                        onClick={() => addSellerItemCopy(item)}
                      />
                    )}
                    {salesEditable && isSellerItemLeader(item) && canDeleteSellerItem(item) && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="商品を削除"
                        title="商品を削除"
                        onClick={() => requestRemoveSellerItem(item)}
                      />
                    )}
                    {salesEditable && isSellerItemLeader(item) && (
                      <VoiceInput
                        onCompleted={({ transcript, language }) => {
                          console.log("Voice input completed", {
                            itemId: item.propose_item_id,
                            transcript,
                            language,
                          });
                        }}
                      >
                        {(voiceControls) => {
                          const isActive =
                            activeVoiceItemIndex === item.propose_item_id;
                          return (
                            <Button
                              type="text"
                              icon={
                                isActive && voiceControls.isRecording ? (
                                  <StopOutlined />
                                ) : (
                                  <AudioOutlined />
                                )
                              }
                              aria-label={
                                isActive && voiceControls.isRecording
                                  ? "音声入力を停止"
                                  : "音声入力を開始"
                              }
                              disabled={
                                voiceControls.isPanelOpen &&
                                (!isActive || !voiceControls.isRecording)
                              }
                              onClick={() => {
                                if (isActive && voiceControls.isRecording) {
                                  voiceControls.stopRecording();
                                  return;
                                }
                                if (!voiceControls.isPanelOpen) {
                                  setActiveVoiceItemIndex(item.propose_item_id);
                                  void voiceControls.startRecording();
                                }
                              }}
                            />
                          );
                        }}
                      </VoiceInput>
                    )}
                    <Button
                      type="text"
                      icon={<FileTextOutlined />}
                      aria-label={
                        itemNoteRows.has(item.propose_item_id!)
                          ? "備考を閉じる"
                          : "備考を表示"
                      }
                      onClick={() => toggleNote(item.propose_item_id!)}
                    />
                  </Space>
                ),
              },
            ]}
          />
        )}
      </section>
      <section className="propose-section">
        <h3
          className="propose-collapsible-section-title"
          role="button"
          tabIndex={0}
          onClick={() => setPart3Collapsed((collapsed) => !collapsed)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setPart3Collapsed((collapsed) => !collapsed);
            }
          }}
        >
          パート3 — 日別供給量スケジュール
          <Button
            type="text"
            size="small"
            icon={part3Collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              setPart3Collapsed((collapsed) => !collapsed);
            }}
          >
            {part3Collapsed ? "表示" : "非表示"}
          </Button>
        </h3>
        {!part3Collapsed && (
          <Table
            className="propose-edit-table"
            rowKey="propose_item_id"
            pagination={false}
            scroll={{ x: Math.max(620, 160 + dates.length * 168) }}
            dataSource={propose.items}
            columns={[
              {
                title: "商品",
                dataIndex: "product_code",
                fixed: "left",
                width: 160,
                render: (value, item) => (
                  <strong>{ellipsisText(`${value} - ${text(item.product_name)}`)}</strong>
                ),
              },
              ...dates.map((deliveryDate, dateIndex) => ({
                title: scheduleDateTitle(deliveryDate),
                width: 168,
                align: "center" as const,
                render: (
                  _: unknown,
                  item: Propose["items"][number],
                  rowIndex: number,
                ) => {
                  const schedule = item.schedules.find(
                    (schedule) => schedule.delivery_date === deliveryDate,
                  );
                  const quantity = schedule?.quantity ?? 0;
                  return salesEditable && isSellerItemLeader(item) ? (
                    <div className="propose-schedule-price-inputs">
                      <Input
                        id={`propose-schedule-input-${rowIndex}-${dateIndex}-quantity`}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="数量"
                        value={quantity || ""}
                        onKeyDown={(event) =>
                          moveScheduleInputFocus(
                            event,
                            rowIndex,
                            dateIndex,
                            "quantity",
                          )
                        }
                        onChange={(event) =>
                          updateScheduleField(
                            item,
                            deliveryDate,
                            "quantity",
                            event.target.value,
                          )
                        }
                      />
                      <Input
                        id={`propose-schedule-input-${rowIndex}-${dateIndex}-wholesale_price`}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="仕切価格"
                        disabled={!quantity}
                        value={schedule?.wholesale_price ?? ""}
                        onKeyDown={(event) =>
                          moveScheduleInputFocus(
                            event,
                            rowIndex,
                            dateIndex,
                            "wholesale_price",
                          )
                        }
                        onChange={(event) =>
                          updateScheduleField(
                            item,
                            deliveryDate,
                            "wholesale_price",
                            event.target.value,
                          )
                        }
                      />
                      <Input
                        id={`propose-schedule-input-${rowIndex}-${dateIndex}-price`}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="提案価格"
                        disabled={!quantity}
                        value={schedule?.price ?? ""}
                        onKeyDown={(event) =>
                          moveScheduleInputFocus(
                            event,
                            rowIndex,
                            dateIndex,
                            "price",
                          )
                        }
                        onChange={(event) =>
                          updateScheduleField(
                            item,
                            deliveryDate,
                            "price",
                            event.target.value,
                          )
                        }
                      />
                    </div>
                  ) : (
                    <div className="propose-schedule-price-values">
                      <span>数量: {number(schedule?.quantity)}</span>
                      <span>仕切: {number(schedule?.wholesale_price)}</span>
                      <span>提案: {number(schedule?.price)}</span>
                    </div>
                  );
                },
              })),
            ]}
          />
        )}
      </section>
      <Modal
        open={Boolean(submissionHistoryItem)}
        title={`担当営業・提出履歴: ${submissionHistoryItem?.product_code ?? ""}`}
        footer={null}
        onCancel={() => setSubmissionHistoryItem(undefined)}
      >
        {(submissionHistoryItem?.sellers ?? []).map((seller) => (
          <div key={seller.seller_id} style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
            <strong>{seller.display_name || `営業 ${seller.seller_id}`}</strong>
            <div>{seller.submitted_at ? `提出済み: ${dayjs(seller.submitted_at).format("YYYY/MM/DD HH:mm")}` : "未提出"}</div>
          </div>
        ))}
      </Modal>
    </main>
  );
};
